import { useQuery } from "@tanstack/react-query";
import { billing } from "@/lib/billing";
import { api } from "@/lib/api";
import { pick, asList, fmtDateTime } from "@/lib/admin-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_VIEW = {
  active: { label: "Attivo", cls: "bg-emerald-50 text-emerald-700" },
  trialing: { label: "Prova", cls: "bg-volt-soft text-volt" },
  past_due: { label: "Pagamento fallito", cls: "bg-amber-50 text-amber-700" },
  canceled: { label: "Cancellato", cls: "bg-slate-100 text-slate-500" },
};

export function AdminSubscriptions() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: async () => (await billing.get("/admin/subscriptions")).data,
  });
  const { data: prosData } = useQuery({
    queryKey: ["admin-pros"],
    queryFn: async () => (await api.get("/admin/professionals")).data,
  });

  const subs = data?.subscriptions || [];
  const subsByUser = Object.fromEntries(subs.map((s) => [s.user_id, s]));
  const subsByEmail = Object.fromEntries(subs.filter((s) => s.email).map((s) => [s.email, s]));
  const pros = asList(prosData);

  const rows = [
    ...pros.map((p) => {
      const s = subsByUser[p.user_id || p.id] || subsByEmail[p.email];
      return {
        key: p.user_id || p.id,
        name: pick(p, ["name", "business_name"], "—"),
        email: pick(p, ["email"], "—"),
        sub: s || null,
      };
    }),
    ...subs
      .filter((s) => !pros.some((p) => (p.user_id || p.id) === s.user_id || p.email === s.email))
      .map((s) => ({ key: s.user_id, name: "—", email: s.email || s.user_id, sub: s })),
  ];

  const proCount = rows.filter((r) => r.sub?.pro_active).length;

  return (
    <div className="px-4 sm:px-6 pt-5 space-y-4" data-testid="admin-subscriptions-page">
      <div className="flex items-end justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Abbonamenti</h1>
        <p className="text-sm text-slate-500" data-testid="subs-summary">{proCount} PRO / {rows.length} professionisti</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
      ) : rows.length ? (
        <div className="space-y-3" data-testid="subscriptions-list">
          {rows.map((r) => {
            const s = r.sub;
            const view = s ? (STATUS_VIEW[s.status] || { label: s.status, cls: "bg-slate-100 text-slate-500" }) : null;
            return (
              <div key={r.key} data-testid={`sub-row-${r.key}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {s?.pro_active ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : s?.status === "past_due" ? <Clock className="w-5 h-5 text-amber-500 shrink-0" /> : <XCircle className="w-5 h-5 text-slate-300 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">{r.name}</p>
                      <p className="text-xs text-slate-500 truncate">{r.email}</p>
                    </div>
                  </div>
                  {s?.pro_active ? (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-whoo text-white" data-testid={`pro-badge-${r.key}`}>PRO</span>
                  ) : (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500" data-testid={`free-badge-${r.key}`}>FREE</span>
                  )}
                </div>
                {s ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>Piano: <b className="text-slate-700">{s.plan === "yearly" ? "Annuale" : "Mensile"}</b></span>
                    <span>Stato: <span className={`font-bold px-1.5 py-0.5 rounded-full ${view.cls}`}>{view.label}</span></span>
                    <span>{s.cancel_at_period_end ? "Termina il" : "Rinnovo"}: <b className="text-slate-700">{fmtDateTime(s.current_period_end)}</b></span>
                    <span>Inizio: <b className="text-slate-700">{fmtDateTime(s.current_period_start)}</b></span>
                    <span className="col-span-2 break-all">Customer: {s.stripe_customer_id || "—"}</span>
                    <span className="col-span-2 break-all">Subscription: {s.stripe_subscription_id || "—"}</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Nessun abbonamento: utilizzo gratuito della piattaforma.</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center" data-testid="subscriptions-empty">
          <p className="text-sm text-slate-500">Nessun professionista registrato.</p>
        </div>
      )}
    </div>
  );
}
